import React, { useState } from 'react';
import { FileText } from 'lucide-react';
import { CURRENCIES } from '../../utils/constants';
import { getPropertyDocumentUrl } from '../../services/supabase/propertyStorage';

export default function LeaseForm({ onSave, onCancel, lease = null, propertyId }) {
  const isEditMode = !!lease;
  const [isLoadingDocument, setIsLoadingDocument] = useState(false);

  const [formData, setFormData] = useState({
    tenantName: lease?.tenant_name || '',
    tenantEmail: lease?.tenant_email || '',
    tenantPhone: lease?.tenant_phone || '',
    startDate: lease?.start_date || '',
    endDate: lease?.end_date || '',
    rentalAmount: lease?.rental_amount || '',
    rentalCurrency: lease?.rental_currency || 'PLN',
    paymentDay: lease?.payment_day || '',
    depositAmount: lease?.deposit_amount || '',
    notes: lease?.notes || '',
    contractAttachment: null,
    contractAttachmentPath: lease?.contract_attachment_path || null,
  });

  const handleSubmit = async (e) => {
    e.preventDefault();

    const leaseData = {
      propertyId,
      leaseId: lease?.id,
      ...formData,
    };

    await onSave(leaseData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Dane najemcy */}
      <div className="border-b border-slate-200 pb-6">
        <h3 className="text-lg font-semibold text-slate-800 mb-4">Dane najemcy</h3>

        <div className="mb-4">
          <label className="block text-sm font-medium text-slate-700 mb-2">
            Imię i nazwisko *
          </label>
          <input
            type="text"
            value={formData.tenantName}
            onChange={(e) => setFormData({ ...formData, tenantName: e.target.value })}
            placeholder="Jan Kowalski"
            required
            className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-violet-500 focus:border-transparent outline-none transition-all"
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Email</label>
            <input
              type="email"
              value={formData.tenantEmail}
              onChange={(e) => setFormData({ ...formData, tenantEmail: e.target.value })}
              placeholder="jan@example.com"
              className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-violet-500 focus:border-transparent outline-none transition-all"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Telefon</label>
            <input
              type="tel"
              value={formData.tenantPhone}
              onChange={(e) => setFormData({ ...formData, tenantPhone: e.target.value })}
              placeholder="+48 123 456 789"
              className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-violet-500 focus:border-transparent outline-none transition-all"
            />
          </div>
        </div>
      </div>

      {/* Okres najmu */}
      <div className="border-b border-slate-200 pb-6">
        <h3 className="text-lg font-semibold text-slate-800 mb-4">Okres najmu</h3>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Data rozpoczęcia *
            </label>
            <input
              type="date"
              value={formData.startDate}
              onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
              required
              className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-violet-500 focus:border-transparent outline-none transition-all"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Data zakończenia
            </label>
            <input
              type="date"
              value={formData.endDate}
              onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
              className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-violet-500 focus:border-transparent outline-none transition-all"
            />
            <p className="text-xs text-slate-500 mt-1">
              Puste pole oznacza umowę na czas nieokreślony
            </p>
          </div>
        </div>
      </div>

      {/* Warunki finansowe */}
      <div className="border-b border-slate-200 pb-6">
        <h3 className="text-lg font-semibold text-slate-800 mb-4">Warunki finansowe</h3>

        <div className="grid grid-cols-3 gap-4 mb-4">
          <div className="col-span-2">
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Kwota najmu *
            </label>
            <input
              type="number"
              step="0.01"
              value={formData.rentalAmount}
              onChange={(e) => setFormData({ ...formData, rentalAmount: e.target.value })}
              placeholder="2500.00"
              required
              className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-violet-500 focus:border-transparent outline-none transition-all"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Waluta</label>
            <select
              value={formData.rentalCurrency}
              onChange={(e) => setFormData({ ...formData, rentalCurrency: e.target.value })}
              className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-violet-500 focus:border-transparent outline-none transition-all"
            >
              {Object.entries(CURRENCIES).map(([code, { label }]) => (
                <option key={code} value={code}>{label}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Dzień płatności (miesiąca)
            </label>
            <input
              type="number"
              min="1"
              max="31"
              value={formData.paymentDay}
              onChange={(e) => setFormData({ ...formData, paymentDay: e.target.value })}
              placeholder="10"
              className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-violet-500 focus:border-transparent outline-none transition-all"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Kaucja</label>
            <input
              type="number"
              step="0.01"
              value={formData.depositAmount}
              onChange={(e) => setFormData({ ...formData, depositAmount: e.target.value })}
              placeholder="5000.00"
              className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-violet-500 focus:border-transparent outline-none transition-all"
            />
          </div>
        </div>
      </div>

      {/* Załącznik umowy */}
      <div className="border-b border-slate-200 pb-6">
        <label className="block text-sm font-medium text-slate-700 mb-2">
          Załącznik umowy (PDF lub obraz)
        </label>
        <div className="flex items-center gap-4">
          <label className="flex-1 cursor-pointer">
            <div className="border-2 border-dashed border-slate-300 rounded-xl p-4 hover:border-blue-500 transition-colors">
              <div className="flex items-center gap-3">
                <FileText className="w-5 h-5 text-slate-400" />
                <div className="flex-1">
                  {formData.contractAttachment ? (
                    <p className="text-sm text-slate-700">{formData.contractAttachment.name}</p>
                  ) : formData.contractAttachmentPath ? (
                    <p className="text-sm text-slate-700">Dokument przesłany</p>
                  ) : (
                    <p className="text-sm text-slate-500">Kliknij, aby dodać plik (PDF, JPG, PNG)</p>
                  )}
                </div>
              </div>
            </div>
            <input
              type="file"
              accept=".pdf,application/pdf,image/jpeg,image/jpg,image/png"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) {
                  setFormData({ ...formData, contractAttachment: file });
                }
              }}
              className="hidden"
            />
          </label>
          {(formData.contractAttachment || formData.contractAttachmentPath) && (
            <button
              type="button"
              onClick={() => setFormData({ ...formData, contractAttachment: null, contractAttachmentPath: null })}
              className="px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors"
            >
              Usuń
            </button>
          )}
        </div>
        <p className="text-xs text-slate-500 mt-1">PDF, JPG lub PNG. Maksymalnie 10MB.</p>

        {/* Info o dokumencie - bez podglądu w trybie edycji aby uniknąć problemów */}
        {formData.contractAttachmentPath && (
          <div className="mt-3">
            <p className="text-sm text-slate-600">
              ✓ Dokument został przesłany. Zamknij okno edycji aby zobaczyć podgląd.
            </p>
          </div>
        )}
      </div>

      {/* Notatki */}
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">Notatki</label>
        <textarea
          value={formData.notes}
          onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
          placeholder="Dodatkowe informacje o umowie..."
          rows="4"
          className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-violet-500 focus:border-transparent outline-none transition-all"
        />
      </div>

      {/* Przyciski */}
      <div className="flex gap-3">
        <button
          type="button"
          onClick={onCancel}
          className="px-6 py-3 border border-slate-300 text-slate-700 rounded-xl hover:bg-slate-50 transition-all duration-300 font-medium"
        >
          Anuluj
        </button>
        <button
          type="submit"
          className="flex-1 px-6 py-3 bg-gradient-to-r from-violet-600 to-purple-600 text-white rounded-xl hover:shadow-lg hover:shadow-violet-500/30 transition-all duration-300 font-medium"
        >
          {isEditMode ? 'Zaktualizuj umowę' : 'Dodaj umowę'}
        </button>
      </div>
    </form>
  );
}
